import React, { useEffect, useState } from "react"
import { connect, useDispatch, useSelector } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import { ModernButton, ModernCard } from '../../components/ModernUI'
import {
    User,
    Mail,
    Shield,
    Calendar,
    Edit2,
    Save,
    X,
} from 'lucide-react'
import { handleErrors, showToaster } from "components/Common/common"
import axiosInstance from "components/Common/ApiManagement/axiosInstance"
import { ApiNames } from "components/Common/ApiManagement/apiServices"
import { getUser } from "../../store/users/actions"
import "./Profile.css"

const Profile = (props) => {
    document.title = "My Profile | GOVCOM"

    const authUser = useSelector((state) => state?.users?.users)
    const dispatch = useDispatch()

    const [profileUser, setProfileUser] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const [editData, setEditData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        department: '',
        designation: '',
        website: ''
    })

    useEffect(() => {
        props.setBreadcrumbItems('My Profile', [])
    }, [])

    // Fetch profile on mount
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { data: res } = await axiosInstance.get('user/me')
                const rawUser = res.data

                const currentUser = {
                    _id: rawUser._id,
                    userId: rawUser._id,
                    username: rawUser.UserName,
                    email: rawUser.UserEmail,
                    firstname: rawUser.FirstName,
                    lastname: rawUser.LastName,
                    userType: rawUser.UserType,
                    permissions: rawUser.Permissions,
                    __v: rawUser.__v,
                    createdAt: rawUser.createdAt,
                    branch: rawUser.branch || null,

                    phone: rawUser.UserPhoneNumber || '',
                    department: rawUser.Department || '',
                    designation: rawUser.Designation || '',
                    website: rawUser.Website || ''
                }

                setProfileUser(currentUser)

                setEditData({
                    firstname: currentUser.firstname || '',
                    lastname: currentUser.lastname || '',
                    email: currentUser.email || '',
                    phone: currentUser.phone || '',
                    department: currentUser.department || '',
                    designation: currentUser.designation || '',
                    website: currentUser.website || ''
                })

                // Sync redux once
                dispatch(getUser(currentUser))

            } catch (err) {
                console.error("Failed to fetch user profile:", err)
                showToaster(false, "Session expired. Please login again.")
            }
        }

        fetchUserData()
    }, [])

    const userType = profileUser?.userType || authUser?.userType

    const getInitials = (name) => {
        if (!name) return "U"
        const names = name.split(' ')
        if (names.length >= 2) return (names[0][0] + names[1][0]).toUpperCase()
        return name.substring(0, 2).toUpperCase()
    }

    const getFullName = () => {
        if (!profileUser) return "User"
        if (profileUser.firstname && profileUser.lastname) {
            return `${profileUser.firstname} ${profileUser.lastname}`
        }
        return profileUser.username || "User"
    }

    const handleEdit = () => setIsEditing(true)

    const handleCancel = () => {
        setIsEditing(false)
        if (profileUser) {
            setEditData({
                firstname: profileUser.firstname || '',
                lastname: profileUser.lastname || '',
                email: profileUser.email || '',
                phone: profileUser.phone || '',
                department: profileUser.department || '',
                designation: profileUser.designation || '',
                website: profileUser.website || ''
            })
        }
    }

    const handleSave = async () => {
        try {
            const userId = profileUser?._id || authUser?._id
            if (!userId) {
                showToaster(false, "User information not available. Please refresh the page.")
                return
            }

            if (!editData.firstname?.trim() || !editData.lastname?.trim() || !editData.email?.trim()) {
                showToaster(false, "Please fill in all required fields.")
                return
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(editData.email)) {
                showToaster(false, "Please enter a valid email address.")
                return
            }

            // Client-side Phone Validation
            if (['standard', 'organizationadmin'].includes(userType) && editData.phone) {
                const usaPhoneRegex = /^(\+?1[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
                if (!usaPhoneRegex.test(editData.phone)) {
                    showToaster(false, "Invalid phone number format. accepted formats: (123) 456-7890, 123-456-7890, 1234567890")
                    return
                }
            }



            setIsSaving(true)

            const userVersion = profileUser?.__v || authUser?.__v || 0
            const fullName = `${editData.firstname} ${editData.lastname}`.trim()

            const updateData = {
                username: fullName || profileUser.username,
                firstname: editData.firstname,
                lastname: editData.lastname,
                email: editData.email,
            }

            // Standard + Organization
            if (['standard', 'organizationadmin'].includes(userType)) {
                updateData.phoneNumber = editData.phone
                updateData.department = editData.department
            }

            // Organization only
            if (userType === 'organizationadmin') {
                updateData.designation = editData.designation
                updateData.website = editData.website
            }

            const { data } = await axiosInstance.put(
                `${ApiNames.USER_UPDATE}/${userId}`,
                updateData,
                {
                    headers: {
                        'If-Match': userVersion,
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (data.status) {
                const updatedRaw = data.data

                const updatedUser = {
                    _id: updatedRaw._id,
                    userId: updatedRaw._id,
                    // username: updatedRaw.UserName,
                    email: updatedRaw.UserEmail,
                    firstname: updatedRaw.FirstName,
                    lastname: updatedRaw.LastName,
                    userType: updatedRaw.UserType,
                    permissions: updatedRaw.Permissions,
                    __v: updatedRaw.__v,
                    createdAt: updatedRaw.createdAt,
                    branch: updatedRaw.branch || null,

                    phone: updatedRaw.UserPhoneNumber || '',
                    department: updatedRaw.Department || '',
                    designation: updatedRaw.Designation || '',
                    website: updatedRaw.Website || ''
                }

                showToaster(true, data.message || "Profile updated successfully")
                setIsEditing(false)

                setProfileUser(updatedUser)

                setEditData({
                    firstname: updatedUser.firstname,
                    lastname: updatedUser.lastname,
                    // username: updatedUser.firstname ? `${updatedUser.firstname} ${updatedUser.lastname}` : '-',
                    email: updatedUser.email,
                    phone: updatedUser.phone,
                    department: updatedUser.department,
                    designation: updatedUser.designation,
                    website: updatedUser.website
                })

                dispatch(getUser(updatedUser))
            }

        } catch (error) {
            console.error('Save error:', error)
            handleErrors(error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setEditData(prev => ({ ...prev, [name]: value }))
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    return (
        <div className="profile-page-container">
            <div className="profile-page-content">

                {/* Header */}
                <div className="profile-header">
                    <div className="profile-header-content">
                        <div className="profile-avatar-large">
                            {getInitials(getFullName())}
                        </div>
                        <div className="profile-header-info">
                            <h1 className="profile-header-name">{getFullName()}</h1>
                            <div className="profile-header-role">
                                <Shield className="w-4 h-4" />
                                <span>{userType || 'User'}</span>
                            </div>
                        </div>
                    </div>

                    {!isEditing && (
                        <ModernButton
                            variant="primary"
                            icon={Edit2}
                            size="md"
                            onClick={handleEdit}
                        >
                            Edit Profile
                        </ModernButton>
                    )}
                </div>

                {/* Profile Card */}
                <div className="profile-cards-grid" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
                    <ModernCard
                        title="Personal Information"
                        subtitle="Manage your personal details"
                        icon={User}
                    >
                        <div className="profile-info-grid">

                            {/* First Name */}
                            <div className="profile-info-item">
                                <label className="profile-info-label">First Name</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="firstname"
                                        value={editData.firstname}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <div className="profile-info-value">{profileUser?.firstname || 'N/A'}</div>
                                )}
                            </div>

                            {/* Last Name */}
                            <div className="profile-info-item">
                                <label className="profile-info-label">Last Name</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="lastname"
                                        value={editData.lastname}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <div className="profile-info-value">{profileUser?.lastname || 'N/A'}</div>
                                )}
                            </div>

                            {/* Email */}
                            <div className="profile-info-item">
                                <label className="profile-info-label">
                                    <Mail className="w-4 h-4 inline mr-1" /> Email Address
                                </label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={editData.email}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <div className="profile-info-value">{profileUser?.email || 'N/A'}</div>
                                )}
                            </div>

                            {/* Phone */}
                            {['standard', 'organizationadmin'].includes(userType) && (
                                <div className="profile-info-item">
                                    <label className="profile-info-label">Phone Number</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="phone"
                                            value={editData.phone}
                                            onChange={handleInputChange}
                                            className="profile-input"
                                            placeholder="(123) 456-7890"
                                        />
                                    ) : (
                                        <div className="profile-info-value">{profileUser?.phone || 'N/A'}</div>
                                    )}
                                </div>
                            )}

                            {/* Department */}
                            {['standard', 'organizationadmin'].includes(userType) && (
                                <div className="profile-info-item">
                                    <label className="profile-info-label">Department</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="department"
                                            value={editData.department}
                                            onChange={handleInputChange}
                                            className="profile-input"
                                        />
                                    ) : (
                                        <div className="profile-info-value">{profileUser?.department || 'N/A'}</div>
                                    )}
                                </div>
                            )}

                            {/* Designation (Organization only) */}
                            {userType === 'organizationadmin' && (
                                <div className="profile-info-item">
                                    <label className="profile-info-label">Designation</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="designation"
                                            value={editData.designation}
                                            onChange={handleInputChange}
                                            className="profile-input"
                                        />
                                    ) : (
                                        <div className="profile-info-value">{profileUser?.designation || 'N/A'}</div>
                                    )}
                                </div>
                            )}

                            {/* Website (Organization only) */}
                            {userType === 'organizationadmin' && (
                                <div className="profile-info-item">
                                    <label className="profile-info-label">Organization Website</label>
                                    {isEditing ? (
                                        <input
                                            type="url"
                                            name="website"
                                            value={editData.website}
                                            onChange={handleInputChange}
                                            className="profile-input"
                                            placeholder="https://www.yoursite.com"
                                        />
                                    ) : (
                                        <div className="profile-info-value">
                                            {profileUser?.website ? (
                                                <a
                                                    href={profileUser.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {profileUser.website}
                                                </a>
                                            ) : 'N/A'}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="profile-info-item"> <label className="profile-info-label"> <Shield className="w-4 h-4 inline mr-1" /> User Type </label> <div className="profile-info-value"> {profileUser?.userType || authUser?.userType || 'N/A'}</div> </div> {profileUser?.branch && (<div className="profile-info-item"> <label className="profile-info-label">Organization</label> <div className="profile-info-value">{profileUser.branch}</div> </div>)}
                            {/* Member Since */}
                            {profileUser?.createdAt && (
                                <div className="profile-info-item">
                                    <label className="profile-info-label">
                                        <Calendar className="w-4 h-4 inline mr-1" /> Member Since
                                    </label>
                                    <div className="profile-info-value">{formatDate(profileUser.createdAt)}</div>
                                </div>
                            )}

                        </div>

                        {/* Actions */}
                        {isEditing && (
                            <div className="profile-actions">
                                <ModernButton
                                    variant="primary"
                                    icon={Save}
                                    size="md"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </ModernButton>
                                <ModernButton
                                    variant="ghost"
                                    icon={X}
                                    size="md"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </ModernButton>
                            </div>
                        )}
                    </ModernCard>
                </div>
            </div>
        </div>
    )
}

export default connect(null, { setBreadcrumbItems })(Profile)
